// Appointment — full-page service editor (/appointments/services/new,
// /appointments/services/:serviceUid/edit). Two-column layout: details +
// booking rules on the left, weekly availability + closed dates on the right.

import React, { useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import {
  Loader2, Plus, X, CalendarOff, ClipboardList, SlidersHorizontal,
  CalendarClock, Image as ImageIcon, Copy, Info, ArrowLeft,
} from 'lucide-react';
import clsx from 'clsx';
import toast from 'react-hot-toast';
import { Topbar } from '@/components/Topbar';
import { Button } from '@/components/Button';
import { Input } from '@/components/Input';
import { Textarea } from '@/components/Textarea';
import { Modal } from '@/components/Modal';
import { uploadsService } from '@/services/uploads.service';
import {
  appointmentsService, WEEKDAY_NAMES, ymd, to12h,
  type ApptDayHours, type ApptClosure,
} from '@/services/appointments.service';
import TimePicker from './TimePicker';
import SaveProgressOverlay from './SaveProgressOverlay';
import { useLabels, useCompanyCurrency, useCurrencyField } from '@/contexts/WorkspaceContext';

const defaultHours = (): ApptDayHours[] =>
  Array.from({ length: 7 }, (_, weekday) => ({
    weekday,
    isOpen: weekday >= 1 && weekday <= 5, // Mon–Fri 9–6 by default
    windows: weekday >= 1 && weekday <= 5 ? [{ start: '09:00', end: '18:00' }] : [],
  }));

const Section = ({ icon: Icon, title, hint, children }: { icon: typeof Info; title: string; hint?: string; children: React.ReactNode }) => (
  <div className="rounded-2xl border border-border bg-surface overflow-hidden">
    <div className="px-5 py-3 border-b border-border bg-surface-alt/40 flex items-center gap-2.5">
      <span className="w-7 h-7 rounded-lg bg-primary-soft text-primary flex items-center justify-center"><Icon size={14} /></span>
      <div>
        <div className="text-[13px] font-bold text-text-primary leading-tight">{title}</div>
        {hint && <div className="text-[11px] text-text-muted mt-[1px]">{hint}</div>}
      </div>
    </div>
    <div className="p-5">{children}</div>
  </div>
);

const Field = ({ label, hint, right, error, children, className }: { label: string; hint?: string; right?: React.ReactNode; error?: string; children: React.ReactNode; className?: string }) => (
  <div className={className}>
    {/* caption row: label left, hint / counter right */}
    <div className="flex items-baseline justify-between gap-2 mb-1 min-w-0">
      <label className="text-[11px] font-semibold uppercase tracking-wide text-text-muted shrink-0">{label}</label>
      {right ?? (hint && <span className="text-[10.5px] text-text-muted/80 truncate">{hint}</span>)}
    </div>
    {children}
    {error && <div className="text-[11px] text-danger font-medium mt-1">{error}</div>}
  </div>
);

/** caption-row character counter — reddens at the limit */
const CharCount = ({ len, max }: { len: number; max: number }) => (
  <span className={clsx('text-[10.5px] tabular-nums shrink-0', len >= max ? 'text-danger font-semibold' : 'text-text-muted/80')}>
    {len}/{max}
  </span>
);

const ServiceEditorPage = () => {
  const companyCurrency = useCompanyCurrency();
  const t = useLabels();
  const navigate = useNavigate();
  const { serviceUid } = useParams<{ serviceUid: string }>();
  const edit = !!serviceUid;

  const [loading, setLoading] = useState(edit);
  const [saving, setSaving] = useState(false);

  const [name, setName] = useState('');
  const [description, setDescription] = useState('');
  const [imageUrl, setImageUrl] = useState('');           // stored URL (already uploaded)
  const [pendingImage, setPendingImage] = useState<File | null>(null); // picked, uploads on confirm
  const [pendingPreview, setPendingPreview] = useState(''); // local object URL for preview
  useEffect(() => () => { if (pendingPreview) URL.revokeObjectURL(pendingPreview); }, [pendingPreview]);
  const [duration, setDuration] = useState(30);
  const [price, setPrice] = useState('');
  const [currency, setCurrency] = useCurrencyField();
  const [capacity, setCapacity] = useState(1);
  const [advanceDays, setAdvanceDays] = useState(30);
  const [leadMinutes, setLeadMinutes] = useState(0);
  const [bufAfter, setBufAfter] = useState(0);
  const [bufBefore] = useState(0);
  const [active, setActive] = useState(true);
  const [hours, setHours] = useState<ApptDayHours[]>(defaultHours());
  const [closures, setClosures] = useState<ApptClosure[]>([]);

  const [clFrom, setClFrom] = useState('');
  const [clTo, setClTo] = useState('');
  const [clReason, setClReason] = useState('');
  const [clSaving, setClSaving] = useState(false);

  useEffect(() => {
    if (!serviceUid) return;
    setLoading(true);
    appointmentsService.getService(serviceUid)
      .then((r) => {
        const s = r.item;
        setName(s.name); setDescription(s.description || ''); setImageUrl(s.imageUrl || '');
        setDuration(s.durationMinutes); setPrice(s.price != null ? String(s.price) : '');
        setCurrency(s.currency || companyCurrency); setCapacity(s.capacityPerSlot);
        setAdvanceDays(s.maxAdvanceDays); setLeadMinutes(s.minLeadMinutes);
        setBufAfter(s.bufferAfterMin);
        setActive(s.isActive);
        setHours(defaultHours().map((d) => s.hours?.find((h) => h.weekday === d.weekday) ?? { ...d, isOpen: false, windows: [] }));
        setClosures(s.closures ?? []);
      })
      .catch(() => { toast.error("Couldn't load service"); navigate('/appointments/services'); })
      .finally(() => setLoading(false));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [serviceUid]);

  const [errors, setErrors] = useState<{ name?: string; availability?: string; windows: Record<string, string> }>({ windows: {} });

  const patchDay = (weekday: number, patch: Partial<ApptDayHours>) => {
    setHours((h) => h.map((d) => (d.weekday === weekday ? { ...d, ...patch } : d)));
    setErrors((p) => (p.availability ? { ...p, availability: undefined } : p));
  };

  const copyToOpenDays = (weekday: number) => {
    const src = hours.find((d) => d.weekday === weekday);
    if (!src) return;
    setHours((h) => h.map((d) => (d.isOpen && d.weekday !== weekday ? { ...d, windows: src.windows.map((w) => ({ ...w })) } : d)));
    toast.success('Copied to all open days');
  };

  const [confirmOpen, setConfirmOpen] = useState(false);

  /// validate → highlight fields → open the detailed confirmation; the actual
  /// save runs on confirm
  const save = () => {
    const errs: { name?: string; availability?: string; windows: Record<string, string> } = { windows: {} };
    if (!name.trim()) errs.name = 'Service name is required.';
    if (!hours.some((d) => d.isOpen && d.windows.length > 0))
      errs.availability = 'Open at least one day and add a time window.';
    for (const d of hours)
      if (d.isOpen)
        d.windows.forEach((w, i) => {
          if (!w.start) errs.windows[`${d.weekday}-${i}`] = 'Pick a start time.';
          else if (!w.end) errs.windows[`${d.weekday}-${i}`] = 'Pick an end time.';
          else if (w.end <= w.start) errs.windows[`${d.weekday}-${i}`] = 'End time must be after the start time.';
        });

    setErrors(errs);
    if (errs.name || errs.availability || Object.keys(errs.windows).length > 0) {
      toast.error('Please fix the highlighted fields');
      return;
    }
    setConfirmOpen(true);
  };

  const [overlayVisible, setOverlayVisible] = useState(false);
  const [overlayPhase, setOverlayPhase] = useState<'processing' | 'complete'>('processing');
  const MIN_LOADER_DURATION = 1000;

  const doSave = async () => {
    const startTime = Date.now();
    setConfirmOpen(false);
    setOverlayPhase('processing');
    setOverlayVisible(true);
    setSaving(true);

    // deferred image upload — via the ImageKit proxy
    let finalImageUrl = imageUrl;
    if (pendingImage) {
      try {
        const up = await uploadsService.imagekit(pendingImage, 'appointment-services');
        finalImageUrl = up.url;
      } catch {
        setSaving(false);
        setOverlayVisible(false);
        toast.error('Image upload failed — the service was NOT saved, try again.');
        return;
      }
    }

    const body = {
      name: name.trim(), description, imageUrl: finalImageUrl,
      durationMinutes: duration,
      price: price.trim() === '' ? null : Number(price),
      currency: price.trim() === '' ? undefined : currency,
      capacityPerSlot: capacity, maxAdvanceDays: advanceDays, minLeadMinutes: leadMinutes,
      bufferBeforeMin: bufBefore, bufferAfterMin: bufAfter, isActive: active,
      hours: hours.map((d) => ({ weekday: d.weekday, isOpen: d.isOpen, windows: d.isOpen ? d.windows : [] })),
    };

    let ok = true;
    let message = '';
    try {
      if (edit) await appointmentsService.updateService(serviceUid!, body);
      else await appointmentsService.createService(body);
    } catch (e: any) {
      ok = false;
      message = e?.response?.data?.message ?? 'Try again.';
    }
    setSaving(false);

    // minimum loader duration — fast saves must not flicker
    const elapsed = Date.now() - startTime;
    if (elapsed < MIN_LOADER_DURATION)
      await new Promise((resolve) => setTimeout(resolve, MIN_LOADER_DURATION - elapsed));

    if (!ok) {
      setOverlayVisible(false);
      toast.error(`Couldn't save service — ${message}`);
      return;
    }
    setOverlayPhase('complete');
    await new Promise((resolve) => setTimeout(resolve, 600));
    setOverlayVisible(false);
    toast.success(edit ? 'Service updated' : 'Service created');
    navigate('/appointments/services');
  };

  const addClosure = async () => {
    if (!clFrom || !clTo) { toast.error('Pick the closure dates'); return; }
    setClSaving(true);
    try {
      const r = await appointmentsService.closureAdd({ serviceUid: serviceUid!, dateFrom: clFrom, dateTo: clTo, reason: clReason });
      setClosures((c) => [...c, { closureUid: r.closureUid, scope: 'SERVICE', dateFrom: clFrom, dateTo: clTo, reason: clReason }]);
      setClFrom(''); setClTo(''); setClReason('');
    } catch (e: any) {
      toast.error(`Couldn't add closure — ${e?.response?.data?.message ?? 'Try again.'}`);
    }
    setClSaving(false);
  };

  const removeClosure = async (closureUid: string) => {
    try {
      await appointmentsService.closureDelete(closureUid);
      setClosures((c) => c.filter((x) => x.closureUid !== closureUid));
    } catch (e: any) {
      toast.error(`Couldn't remove closure — ${e?.response?.data?.message ?? 'Try again.'}`);
    }
  };

  const num = (v: string, min: number) => Math.max(min, parseInt(v || '0', 10) || min);

  const confirmRow = (label: string, val: React.ReactNode) => (
    <div className="flex justify-between gap-4 py-1.5 border-b border-border/60 last:border-b-0">
      <span className="text-[12px] text-text-muted shrink-0">{label}</span>
      <span className="text-[12.5px] font-medium text-text-primary text-right">{val}</span>
    </div>
  );

  return (
    <>
      <Topbar
        title={edit ? (name || `Edit ${t('services', 'singular').toLowerCase()}`) : `New ${t('services', 'singular').toLowerCase()}`}
        breadcrumb={`${t('appointments')} / ${t('services')}`}
        description="Bookable on the calendar and your public booking link."
        back={
          <button
            onClick={() => navigate('/appointments/services')}
            className="flex h-8 w-8 shrink-0 items-center justify-center rounded-lg text-text-muted hover:bg-surface-alt hover:text-text-primary transition-colors"
            aria-label="Back"
          >
            <ArrowLeft size={16} />
          </button>
        }
      />
      <div className="w-full px-7 py-6">
        {loading ? (
          <div className="flex items-center justify-center gap-2 py-24 text-text-muted"><Loader2 size={18} className="animate-spin" /> Loading service…</div>
        ) : (
          <div className="grid grid-cols-1 lg:grid-cols-[1fr_540px] gap-4 items-start">
            {/* ── left column ── */}
            <div className="flex flex-col gap-4">
              <Section icon={ClipboardList} title="Service details" hint="What customers see when they book">
                <div className="flex gap-5">
                  {/* image — browse shows a LOCAL preview only; the actual upload
                      happens on save confirmation */}
                  <div className="flex flex-col items-center gap-2 shrink-0">
                    {(pendingPreview || imageUrl) ? (
                      <div className="relative">
                        <img src={pendingPreview || imageUrl} alt="" className="w-20 h-20 rounded-2xl object-cover border border-border" />
                        <button
                          type="button"
                          title="Remove image"
                          onClick={() => {
                            if (pendingPreview) URL.revokeObjectURL(pendingPreview);
                            setPendingImage(null); setPendingPreview(''); setImageUrl('');
                          }}
                          className="absolute -top-1.5 -right-1.5 w-5 h-5 rounded-full bg-surface border border-border shadow-soft cursor-pointer flex items-center justify-center text-text-muted hover:text-danger"
                        >
                          <X size={11} />
                        </button>
                      </div>
                    ) : (
                      <div className="w-20 h-20 rounded-2xl bg-primary-soft text-primary flex items-center justify-center"><CalendarClock size={30} strokeWidth={1.5} /></div>
                    )}
                    <label className="inline-flex items-center gap-1.5 text-[11.5px] font-semibold text-primary cursor-pointer">
                      <ImageIcon size={12} /> {(pendingPreview || imageUrl) ? 'Change' : 'Add image'}
                      <input
                        type="file" accept="image/*" className="hidden"
                        onChange={(e) => {
                          const f = e.target.files?.[0];
                          e.target.value = '';
                          if (!f) return;
                          if (pendingPreview) URL.revokeObjectURL(pendingPreview);
                          setPendingImage(f);
                          setPendingPreview(URL.createObjectURL(f));
                        }}
                      />
                    </label>
                    {pendingImage && <span className="text-[10px] text-text-muted">uploads on save</span>}
                  </div>

                  <div className="flex-1 flex flex-col gap-3.5">
                    {/* name + price + currency on one row — price is just a value */}
                    <div className="grid grid-cols-[1fr_110px_104px] gap-3">
                      <Field label="Service name" error={errors.name} right={<CharCount len={name.length} max={200} />}>
                        <Input
                          value={name}
                          maxLength={200}
                          invalid={!!errors.name}
                          onChange={(e) => { setName(e.target.value); if (errors.name) setErrors((p) => ({ ...p, name: undefined })); }}
                          placeholder="e.g. Consultation"
                        />
                      </Field>
                      <Field label="Price">
                        <Input type="number" min={0} value={price} onChange={(e) => setPrice(e.target.value)} placeholder="0" />
                      </Field>
                      <Field label="Currency">
                        <Input
                          value={currency}
                          maxLength={5}
                          disabled={price.trim() === ''}
                          onChange={(e) => setCurrency(e.target.value.toUpperCase())}
                          placeholder={companyCurrency}
                        />
                      </Field>
                    </div>
                    <Field label="Description" right={<CharCount len={description.length} max={1000} />}>
                      <Textarea rows={3} maxLength={1000} value={description} onChange={(e) => setDescription(e.target.value)} placeholder="A short description shown to customers when booking…" />
                    </Field>
                  </div>
                </div>
              </Section>

              <Section icon={SlidersHorizontal} title="Booking rules" hint="How slots are generated and how far ahead customers can book">
                <div className="grid grid-cols-2 md:grid-cols-3 gap-3.5">
                  <Field label="Duration" hint="One appointment">
                    <div className="relative">
                      <Input type="number" min={5} step={5} value={duration} onChange={(e) => setDuration(num(e.target.value, 5))} className="pr-12" />
                      <span className="absolute right-3 top-1/2 -translate-y-1/2 text-[11px] text-text-muted">min</span>
                    </div>
                  </Field>
                  <Field label="Break between slots" hint="Gap after each one">
                    <div className="relative">
                      <Input type="number" min={0} step={5} value={bufAfter} onChange={(e) => setBufAfter(num(e.target.value, 0))} className="pr-12" />
                      <span className="absolute right-3 top-1/2 -translate-y-1/2 text-[11px] text-text-muted">min</span>
                    </div>
                  </Field>
                  <Field label="Book up to" hint="How far ahead">
                    <div className="relative">
                      <Input type="number" min={1} value={advanceDays} onChange={(e) => setAdvanceDays(num(e.target.value, 1))} className="pr-12" />
                      <span className="absolute right-3 top-1/2 -translate-y-1/2 text-[11px] text-text-muted">days</span>
                    </div>
                  </Field>
                  <Field label="Minimum notice" hint="Earliest slot from now">
                    <div className="relative">
                      <Input type="number" min={0} step={15} value={leadMinutes} onChange={(e) => setLeadMinutes(num(e.target.value, 0))} className="pr-12" />
                      <span className="absolute right-3 top-1/2 -translate-y-1/2 text-[11px] text-text-muted">min</span>
                    </div>
                  </Field>
                  <Field label="Max bookings per slot" hint="Same date & time">
                    <Input type="number" min={1} value={capacity} onChange={(e) => setCapacity(num(e.target.value, 1))} />
                  </Field>
                </div>
                {/* live example of the resulting slot pattern */}
                <div className="mt-3 text-[11.5px] text-text-muted bg-surface-alt/50 rounded-lg px-3 py-2">
                  Example: {(() => {
                    const fmt = (m: number) => { const h = Math.floor(m / 60), mm = m % 60, ap = h >= 12 ? 'pm' : 'am'; return `${h % 12 === 0 ? 12 : h % 12}:${String(mm).padStart(2, '0')} ${ap}`; };
                    const step = duration + bufAfter;
                    const s0 = 9 * 60;
                    return [0, 1, 2].map((i) => `${fmt(s0 + i * step)} – ${fmt(s0 + i * step + duration)}`).join('  ·  ');
                  })()} …
                </div>
              </Section>

              {/* actions — below the Booking rules container */}
              <div className="flex justify-end gap-2">
                <Button variant="secondary" onClick={() => navigate('/appointments/services')}>Cancel</Button>
                <Button onClick={save} disabled={saving || loading}>
                  {edit ? 'Save changes' : 'Create service'}
                </Button>
              </div>
            </div>

            {/* ── right column ── */}
            <div className="flex flex-col gap-4">
              <Section icon={CalendarClock} title="Weekly availability" hint="Open days and their time windows — slots are generated inside these">
                {/* week summary */}
                {(() => {
                  const openDays = hours.filter((d) => d.isOpen && d.windows.length > 0);
                  const mins = openDays.flatMap((d) => d.windows).reduce((sum, w) => {
                    if (!w.start || !w.end || w.end <= w.start) return sum;
                    const [sh, sm] = w.start.split(':').map(Number);
                    const [eh, em] = w.end.split(':').map(Number);
                    return sum + (eh * 60 + em - sh * 60 - sm);
                  }, 0);
                  return (
                    <div className="flex items-center gap-2 mb-3">
                      <span className="text-[11.5px] font-semibold text-primary bg-primary-soft rounded-full px-3 py-1">
                        Open {openDays.length} day{openDays.length === 1 ? '' : 's'} / week
                      </span>
                      {mins > 0 && (
                        <span className="text-[11.5px] font-medium text-text-muted bg-surface-alt rounded-full px-3 py-1">
                          {Math.round((mins / 60) * 10) / 10} bookable hours
                        </span>
                      )}
                    </div>
                  );
                })()}

                {errors.availability && (
                  <div className="mb-3 rounded-lg border border-danger/40 bg-danger-soft px-3 py-2 text-[12px] font-medium text-danger">
                    {errors.availability}
                  </div>
                )}
                <div className="flex flex-col gap-2">
                  {hours.map((d) => {
                    const summary = d.isOpen && d.windows.length > 0
                      ? d.windows.filter((w) => w.start && w.end).map((w) => `${to12h(w.start)} – ${to12h(w.end)}`).join('  ·  ')
                      : d.isOpen ? 'No time window yet' : 'Closed';
                    return (
                      <div
                        key={d.weekday}
                        className={clsx(
                          'rounded-xl border transition-colors',
                          d.isOpen ? 'border-primary/25 bg-primary-soft/30' : 'border-border bg-surface-alt/30',
                        )}
                      >
                        {/* day header */}
                        <div className="flex items-center gap-3 px-3 py-2.5">
                          <span className={clsx(
                            'w-9 h-9 rounded-[11px] flex items-center justify-center text-[11px] font-bold uppercase tracking-wide shrink-0 transition-colors',
                            d.isOpen ? 'bg-primary text-on-primary shadow-soft' : 'bg-surface-alt text-text-muted',
                          )}>
                            {WEEKDAY_NAMES[d.weekday].slice(0, 3)}
                          </span>
                          <span className="min-w-0 flex-1">
                            <span className={clsx('block text-[13px] font-semibold', d.isOpen ? 'text-text-primary' : 'text-text-muted')}>
                              {WEEKDAY_NAMES[d.weekday]}
                            </span>
                            <span className="block text-[11px] text-text-muted truncate">{summary}</span>
                          </span>
                          {d.isOpen && d.windows.length > 0 && (
                            <button
                              type="button"
                              title="Copy this day's windows to all open days"
                              onClick={() => copyToOpenDays(d.weekday)}
                              className="border-none bg-transparent cursor-pointer text-text-muted hover:text-primary hover:bg-primary-soft rounded-lg p-2 shrink-0 transition-colors"
                            >
                              <Copy size={14} />
                            </button>
                          )}
                          <button
                            type="button"
                            role="switch"
                            aria-checked={d.isOpen}
                            onClick={() => patchDay(d.weekday, { isOpen: !d.isOpen, windows: !d.isOpen && d.windows.length === 0 ? [{ start: '09:00', end: '18:00' }] : d.windows })}
                            className={clsx(
                              'relative h-[22px] w-10 shrink-0 rounded-full transition-colors',
                              d.isOpen ? 'bg-primary' : 'bg-border-strong',
                            )}
                          >
                            <span className={clsx(
                              'absolute top-[3px] h-4 w-4 rounded-full bg-white shadow-soft transition-all',
                              d.isOpen ? 'left-[21px]' : 'left-[3px]',
                            )} />
                          </button>
                        </div>

                        {/* windows */}
                        {d.isOpen && (
                          <div className="px-3 pb-3 flex flex-col gap-1.5">
                            {d.windows.map((w, i) => {
                              const wkey = `${d.weekday}-${i}`;
                              const werr = errors.windows[wkey];
                              const clearWinErr = () => {
                                if (werr) setErrors((p) => { const wn = { ...p.windows }; delete wn[wkey]; return { ...p, windows: wn }; });
                              };
                              return (
                                <div key={i}>
                                  <div className={clsx(
                                    'flex items-center gap-2 rounded-lg bg-surface border px-2.5 py-1.5',
                                    werr ? 'border-danger' : 'border-border',
                                  )}>
                                    <span className="text-[10px] font-bold text-text-muted/70 w-4 text-center shrink-0">{i + 1}</span>
                                    <TimePicker className="flex-1 min-w-[118px]" value={w.start} onChange={(v) => { clearWinErr(); patchDay(d.weekday, { windows: d.windows.map((x, j) => (j === i ? { ...x, start: v, end: x.end <= v ? '' : x.end } : x)) }); }} />
                                    <span className="text-text-muted/60 text-[11px] shrink-0">→</span>
                                    <TimePicker className="flex-1 min-w-[118px]" value={w.end} min={w.start} onChange={(v) => { clearWinErr(); patchDay(d.weekday, { windows: d.windows.map((x, j) => (j === i ? { ...x, end: v } : x)) }); }} />
                                    <button
                                      type="button"
                                      title="Remove window"
                                      onClick={() => { clearWinErr(); patchDay(d.weekday, { windows: d.windows.filter((_, j) => j !== i) }); }}
                                      className="border-none bg-transparent cursor-pointer text-text-muted/60 hover:text-danger rounded-md p-1.5 shrink-0 transition-colors"
                                    >
                                      <X size={14} />
                                    </button>
                                  </div>
                                  {werr && <div className="text-[11px] text-danger font-medium mt-1 pl-1">{werr}</div>}
                                </div>
                              );
                            })}
                            <button
                              type="button"
                              onClick={() => patchDay(d.weekday, { windows: [...d.windows, d.windows.length === 0 ? { start: '09:00', end: '18:00' } : { start: '14:00', end: '18:00' }] })}
                              className="w-full rounded-lg border border-dashed border-primary/35 hover:border-primary/70 hover:bg-primary-soft/40 bg-transparent cursor-pointer text-primary text-[12px] font-semibold py-[7px] inline-flex items-center justify-center gap-1.5 transition-colors"
                            >
                              <Plus size={13} /> Add time window
                            </button>
                          </div>
                        )}
                      </div>
                    );
                  })}
                </div>
              </Section>

              <Section icon={CalendarOff} title="Closed dates" hint="Holidays or temporary unavailability — no slots on these dates">
                {!edit ? (
                  <div className="flex items-start gap-2 text-[12.5px] text-text-muted">
                    <Info size={14} className="shrink-0 mt-[2px]" />
                    Create the service first — then you can add closed dates here.
                  </div>
                ) : (
                  <div className="flex flex-col gap-2">
                    {closures.map((c) => (
                      <div key={c.closureUid} className="flex items-center gap-2 rounded-lg border border-border px-3 py-2 text-[12.5px]">
                        <span className="font-medium text-text-primary whitespace-nowrap">{c.dateFrom}{c.dateTo !== c.dateFrom ? ` → ${c.dateTo}` : ''}</span>
                        {c.scope === 'COMPANY' && <span className="text-[10px] font-bold uppercase text-text-muted bg-surface-alt rounded-full px-2 py-0.5 shrink-0">All services</span>}
                        <span className="text-text-muted truncate">{c.reason}</span>
                        {c.scope === 'SERVICE' && (
                          <button type="button" onClick={() => void removeClosure(c.closureUid)} className="ml-auto border-none bg-transparent cursor-pointer text-text-muted hover:text-danger p-1"><X size={14} /></button>
                        )}
                      </div>
                    ))}
                    {closures.length === 0 && <div className="text-[12px] text-text-muted">No closed dates yet.</div>}
                    <div className="flex flex-wrap items-center gap-2 pt-1">
                      <Input type="date" className="!h-9 !w-[148px]" min={ymd(new Date())} value={clFrom} onChange={(e) => { setClFrom(e.target.value); if (!clTo || e.target.value > clTo) setClTo(e.target.value); }} />
                      <span className="text-text-muted text-[12px]">to</span>
                      <Input type="date" className="!h-9 !w-[148px]" min={clFrom || ymd(new Date())} value={clTo} onChange={(e) => setClTo(e.target.value)} />
                      <Input className="!h-9 flex-1 min-w-[120px]" placeholder="Reason (optional)" value={clReason} onChange={(e) => setClReason(e.target.value)} />
                      <Button variant="secondary" size="sm" onClick={addClosure} loading={clSaving}>
                        <Plus size={13} />
                      </Button>
                    </div>
                  </div>
                )}
              </Section>
            </div>
          </div>
        )}
      </div>

      {/* detailed save confirmation */}
      <Modal open={confirmOpen} onClose={() => setConfirmOpen(false)} title={edit ? 'Save these changes?' : 'Create this service?'} widthClass="max-w-[480px]">
        {(() => {
          const openDays = hours.filter((d) => d.isOpen && d.windows.length > 0);
          return (
            <div className="flex flex-col gap-3">
              <div className="rounded-xl border border-border px-4 py-2">
                {confirmRow('Service', name.trim())}
                {confirmRow('Price', price.trim() === '' ? 'Free / on request' : `${currency} ${price}`)}
                {confirmRow('Duration', `${duration} minutes${bufAfter > 0 ? ` + ${bufAfter} min break` : ''}`)}
                {confirmRow('Max per slot', `${capacity} booking${capacity > 1 ? 's' : ''} on the same slot`)}
                {confirmRow('Booking window', `up to ${advanceDays} days ahead · min ${leadMinutes} min notice`)}
                {confirmRow('Status', active ? 'Accepting bookings' : 'Paused (not bookable)')}
              </div>
              <div className="rounded-xl border border-border px-4 py-2">
                <div className="text-[11px] font-bold uppercase tracking-wide text-text-muted py-1.5">Weekly availability</div>
                {openDays.map((d) => (
                  <React.Fragment key={d.weekday}>
                    {confirmRow(WEEKDAY_NAMES[d.weekday], d.windows.map((w) => `${to12h(w.start)} – ${to12h(w.end)}`).join(' · '))}
                  </React.Fragment>
                ))}
              </div>
              <div className="text-[11.5px] text-text-muted leading-relaxed">
                {edit
                  ? 'Changed availability applies immediately — already-booked appointments are kept even if they fall outside the new hours.'
                  : 'The service becomes bookable right away on the calendar and your public booking link.'}
              </div>
              <div className="flex justify-end gap-2">
                <Button variant="secondary" onClick={() => setConfirmOpen(false)}>Review again</Button>
                <Button onClick={doSave} loading={saving}>
                  {edit ? 'Confirm & save' : 'Confirm & create'}
                </Button>
              </div>
            </div>
          );
        })()}
      </Modal>

      <SaveProgressOverlay
        isVisible={overlayVisible}
        phase={overlayPhase}
        title={edit ? 'Saving service…' : 'Creating service…'}
        subtitle={pendingImage ? 'Uploading image and saving details…' : 'Saving details and weekly availability…'}
        completeTitle={edit ? 'Service updated!' : 'Service created!'}
        completeSubtitle={edit ? 'Your changes are live.' : "It's now bookable everywhere."}
      />
    </>
  );
};

export default ServiceEditorPage;
